import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Send, Plus, Pencil, Trash2, Power } from "lucide-react";
import { adminGet, adminPost, adminPut, adminDel } from "@/lib/adminApi";

// Bibliothèque de notifications : Julie crée et enregistre à l'avance (rien n'est
// envoyé à la création). Chaque matin à 9h, l'app envoie automatiquement la
// notification du jour, piochée en rotation complète (jamais deux jours de suite
// la même). L'envoi manuel immédiat reste possible à tout moment.
export default function Notifs() {
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [custom, setCustom] = useState("");
  const [customName, setCustomName] = useState("");
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(null); // création / édition dans la bibliothèque

  const load = () =>
    adminGet("/notifications")
      .then((d) => {
        const all = d.notifications || [];
        setTemplates(all.filter((n) => n.status !== "envoyée"));
        setHistory(all.filter((n) => n.status === "envoyée").sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))));
      })
      .catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form?.name?.trim() || !form?.text?.trim()) {
      toast("Donnez un titre et un message.");
      return;
    }
    try {
      const payload = { name: form.name.trim(), text: form.text.trim(), status: form.status === "inactif" ? "inactif" : "actif", channel: "app", frequency: "1×/jour maximum" };
      if (form.id) await adminPut(`/notifications/${form.id}`, payload);
      else await adminPost("/notifications", payload);
      toast(form.id ? "Notification modifiée." : "Enregistrée dans la bibliothèque — elle partira dans la rotation quotidienne.");
      setForm(null);
      load();
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const toggleActive = async (n) => {
    try {
      await adminPut(`/notifications/${n.id}`, { name: n.name, text: n.text, status: n.status === "actif" ? "inactif" : "actif", channel: n.channel || "app", frequency: n.frequency || "1×/jour maximum" });
      toast(n.status === "actif" ? "Notification mise en pause — elle sort de la rotation." : "Notification réactivée — elle reprend sa place dans la rotation.");
      load();
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const remove = async (n) => {
    await adminDel(`/notifications/${n.id}`).catch(() => {});
    toast("Notification retirée de la bibliothèque.");
    load();
  };

  const send = async (n) => {
    try {
      const res = await adminPost(`/notifications/${n.id}/send`, {});
      toast(res.message);
      load();
    } catch {
      toast("Envoi impossible.");
    }
  };

  const sendCustom = async () => {
    if (!custom.trim()) {
      toast("Écrivez votre message d'abord.");
      return;
    }
    setSending(true);
    try {
      const res = await adminPost("/notifications/send-custom", { name: customName, text: custom });
      toast(res.message);
      setCustom("");
      setCustomName("");
      load();
    } catch (err) {
      toast(err.response?.data?.detail || "Envoi impossible.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rav-admin-module" data-testid="admin-notifs">
      <h2 className="rav-admin-h2">Notifications — bibliothèque & envois</h2>
      <p className="rav-page-sub rav-admin-note">
        Enregistrez vos messages à l'avance : chaque matin à 9h, l'application en envoie un automatiquement, en faisant tourner toute la bibliothèque avant de recommencer — jamais deux jours de suite le même. Les messages inactifs sont simplement sautés.
      </p>

      <h3 className="rav-admin-h3">Bibliothèque ({templates.length} message{templates.length > 1 ? "s" : ""})</h3>
      <button type="button" className="rav-primary-button rav-admin-create" data-testid="notif-create" onClick={() => setForm({ name: "", text: "", status: "actif" })}>
        <Plus size={14} strokeWidth={1.8} /> Nouvelle notification
      </button>

      {form && (
        <div className="rav-admin-form" data-testid="notif-form">
          <input
            className="rav-input"
            data-testid="notif-form-name"
            placeholder="Titre (ex. Douceur du matin)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <textarea
            className="rav-input rav-notes-area"
            rows={3}
            data-testid="notif-form-text"
            placeholder="Votre message…"
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
          />
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="notif-form-save" onClick={save}>
              Enregistrer (sans envoyer)
            </button>
            <button type="button" className="rav-text-link" data-testid="notif-form-cancel" onClick={() => setForm(null)}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="rav-admin-list">
        {templates.map((n, i) => (
          <div key={n.id} className="rav-admin-row" data-testid={`notif-row-${i}`}>
            <span className="rav-admin-row-label">
              <Bell size={14} strokeWidth={1.6} style={{ verticalAlign: "-2px", marginRight: 6, color: "var(--rav-gold-dark)" }} />
              {n.name}
              {n.status !== "actif" && <span className="rav-admin-row-sub"> · inactive</span>}
            </span>
            <span className="rav-admin-row-sub">
              « {n.text} »
              {n.last_sent_at ? ` · dernière fois le ${new Date(n.last_sent_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}` : " · jamais envoyée"}
            </span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`notif-send-${i}`} onClick={() => send(n)}>
                <Send size={13} strokeWidth={1.6} /> Envoyer maintenant
              </button>
              <button type="button" className="rav-text-link" data-testid={`notif-edit-${i}`} onClick={() => setForm({ ...n })}>
                <Pencil size={13} strokeWidth={1.6} /> Modifier
              </button>
              <button type="button" className="rav-text-link" data-testid={`notif-toggle-${i}`} onClick={() => toggleActive(n)}>
                <Power size={13} strokeWidth={1.6} /> {n.status === "actif" ? "Désactiver" : "Activer"}
              </button>
              <button type="button" className="rav-text-link rav-note-delete" data-testid={`notif-delete-${i}`} onClick={() => remove(n)}>
                <Trash2 size={13} strokeWidth={1.6} /> Retirer
              </button>
            </span>
          </div>
        ))}
      </div>

      <h3 className="rav-admin-h3">Annonce ponctuelle — envoi immédiat</h3>
      <div className="rav-admin-form">
        <input
          className="rav-input"
          data-testid="notif-custom-name"
          placeholder="Titre (facultatif — ex. Nouveau contenu en ligne)"
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
        />
        <textarea
          className="rav-input rav-notes-area"
          rows={3}
          data-testid="notif-custom-text"
          placeholder="Votre annonce : nouveauté, information particulière…"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
        />
        <button type="button" className="rav-text-link" data-testid="notif-custom-send" disabled={sending} onClick={sendCustom}>
          <Send size={13} strokeWidth={1.6} /> {sending ? "Envoi en cours…" : "Envoyer à toutes les clientes"}
        </button>
      </div>

      {history.length > 0 && (
        <>
          <h3 className="rav-admin-h3">Déjà envoyées</h3>
          <div className="rav-admin-list">
            {history.slice(0, 10).map((n, i) => (
              <div key={n.id} className="rav-admin-row" data-testid={`notif-history-${i}`}>
                <span className="rav-admin-row-label">{n.name}</span>
                <span className="rav-admin-row-sub">
                  « {n.text} » — {n.recipients ?? "?"} destinataire(s)
                  {n.created_at ? ` · ${new Date(n.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
